import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import { getUsuario, crearUsuario, actualizarUsuario, restablecerPassword } from '../../services/usuariosService';
import { getCategoriasAdmin } from '../../services/adminService';
import { getInstitucionesAdmin } from '../../services/institucionesService';
import { useAuth } from '../../context/AuthContext';
import { useInstitucion } from '../../context/InstitucionContext';
import { esSuperAdmin, rolesAsignables, ETIQUETAS_ROL, ROLES } from '../../utils/permisos';

// Alta y edición de usuarios. El rol define qué se pide: los publicadores eligen sus categorías
// raíz; el SuperAdmin además elige la institución (o ninguna, para otro SuperAdmin).
const AdminUsuarioForm = () => {
  const { id } = useParams();
  const esEdicion = Boolean(id);
  const history = useHistory();
  const { usuario: actual } = useAuth();
  const { institucion, paleta: colors } = useInstitucion();
  const superAdmin = esSuperAdmin(actual);

  const [instituciones, setInstituciones] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [form, setForm] = useState({
    nombre: '',
    email: '',
    password: '',
    rol: ROLES.PUBLICADOR,
    institucionId: institucion ? String(institucion.id) : '',
    categoriasRaiz: [],
  });
  const [passwordNueva, setPasswordNueva] = useState('');
  const [loading, setLoading] = useState(esEdicion);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [mensaje, setMensaje] = useState(null);

  const esYo = esEdicion && actual && String(actual.id) === String(id);

  useEffect(() => {
    if (!superAdmin) return;
    getInstitucionesAdmin()
      .then((lista) => setInstituciones(lista.filter((i) => i.activo)))
      .catch(() => setInstituciones([]));
  }, [superAdmin]);

  useEffect(() => {
    if (!esEdicion) return;
    getUsuario(id)
      .then((u) => {
        setForm({
          nombre: u.nombre,
          email: u.email,
          password: '',
          rol: u.rol,
          institucionId: u.institucionId != null ? String(u.institucionId) : '',
          categoriasRaiz: u.categoriasRaiz || [],
        });
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id, esEdicion]);

  // Categorías raíz de la institución elegida (para el SuperAdmin puede ser distinta a la activa).
  const institucionElegida = useMemo(() => {
    if (!superAdmin) return institucion;
    return instituciones.find((i) => String(i.id) === form.institucionId) || null;
  }, [superAdmin, institucion, instituciones, form.institucionId]);

  useEffect(() => {
    if (form.rol !== ROLES.PUBLICADOR || !institucionElegida) {
      setCategorias([]);
      return;
    }
    getCategoriasAdmin(superAdmin ? institucionElegida.slug : undefined)
      .then((lista) => setCategorias(lista.filter((c) => c.padreId == null)))
      .catch(() => setCategorias([]));
  }, [form.rol, institucionElegida, superAdmin]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setMensaje(null);
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const toggleRaiz = (catId) => {
    setForm((prev) => ({
      ...prev,
      categoriasRaiz: prev.categoriasRaiz.includes(catId)
        ? prev.categoriasRaiz.filter((c) => c !== catId)
        : [...prev.categoriasRaiz, catId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setMensaje(null);

    if (!esEdicion && form.password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (form.rol === ROLES.PUBLICADOR && form.categoriasRaiz.length === 0) {
      if (!window.confirm('El publicador no tiene categorías asignadas y no podrá publicar hasta que se le asignen. ¿Continuar?')) return;
    }

    const datos = {
      nombre: form.nombre.trim(),
      email: form.email.trim(),
      rol: form.rol,
      institucionId: form.rol === ROLES.SUPER_ADMIN ? null : (form.institucionId ? Number(form.institucionId) : null),
      categoriasRaiz: form.rol === ROLES.PUBLICADOR ? form.categoriasRaiz : [],
    };

    setGuardando(true);
    try {
      if (esEdicion) {
        await actualizarUsuario(id, datos);
      } else {
        await crearUsuario({ ...datos, password: form.password });
      }
      history.push('/admin/usuarios');
    } catch (err) {
      setError(err.message);
    } finally {
      setGuardando(false);
    }
  };

  const handleRestablecer = async () => {
    setError(null);
    setMensaje(null);
    if (passwordNueva.length < 8) {
      setError('La nueva contraseña debe tener al menos 8 caracteres.');
      return;
    }
    try {
      await restablecerPassword(id, passwordNueva);
      setPasswordNueva('');
      setMensaje('Contraseña restablecida.');
    } catch (err) {
      setError(err.message);
    }
  };

  const inputClass =
    'w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent';
  const ring = { '--tw-ring-color': colors.primary };

  if (loading) {
    return <div className="p-12 text-center text-gray-400">Cargando...</div>;
  }

  return (
    <div className="max-w-2xl">
      <nav className="mb-6 text-sm">
        <Link to="/admin/usuarios" className="font-medium hover:opacity-80" style={{ color: colors.ctaText }}>
          ← Volver a usuarios
        </Link>
      </nav>

      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        {esEdicion ? 'Editar usuario' : 'Nuevo usuario'}
      </h1>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-5">
        {(error || mensaje) && (
          <div
            className={`text-sm rounded-lg px-4 py-3 border ${
              error ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'
            }`}
          >
            {error || mensaje}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Nombre *</label>
            <input type="text" name="nombre" value={form.nombre} onChange={handleChange} required maxLength={150} className={inputClass} style={ring} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Correo electrónico *</label>
            <input type="email" name="email" value={form.email} onChange={handleChange} required maxLength={200} className={inputClass} style={ring} autoComplete="off" />
          </div>
        </div>

        {!esEdicion && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Contraseña *</label>
            <input type="password" name="password" value={form.password} onChange={handleChange} required minLength={8} className={inputClass} style={ring} autoComplete="new-password" />
            <p className="text-xs text-gray-400 mt-1">Mínimo 8 caracteres. Compártela con el usuario por un canal seguro.</p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Rol *</label>
            <select name="rol" value={form.rol} onChange={handleChange} disabled={esYo} className={inputClass} style={ring}>
              {rolesAsignables(actual).map((rol) => (
                <option key={rol} value={rol}>{ETIQUETAS_ROL[rol]}</option>
              ))}
            </select>
            {esYo && <p className="text-xs text-gray-400 mt-1">No puedes cambiar tu propio rol.</p>}
          </div>

          {superAdmin && form.rol !== ROLES.SUPER_ADMIN && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Institución *</label>
              <select name="institucionId" value={form.institucionId} onChange={handleChange} required className={inputClass} style={ring}>
                <option value="">Selecciona una institución</option>
                {instituciones.map((i) => (
                  <option key={i.id} value={i.id}>{i.nombreCorto}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {form.rol === ROLES.PUBLICADOR && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Categorías en las que puede publicar</label>
            <p className="text-xs text-gray-400 mb-2">
              Se asignan categorías raíz; el publicador puede trabajar en toda su jerarquía de subcategorías.
            </p>
            {!institucionElegida ? (
              <p className="text-sm text-gray-400">Elige primero la institución.</p>
            ) : categorias.length === 0 ? (
              <p className="text-sm text-gray-400">La institución no tiene categorías raíz todavía.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {categorias.map((c) => (
                  <label key={c.id} className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={form.categoriasRaiz.includes(c.id)}
                      onChange={() => toggleRaiz(c.id)}
                      style={{ accentColor: colors.primary }}
                    />
                    <span className={c.activo ? 'text-gray-800' : 'text-gray-400'}>
                      {c.nombre}{!c.activo && ' (inactiva)'}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            disabled={guardando}
            className="px-5 py-2.5 text-white font-semibold rounded-lg shadow-md transition-all hover:opacity-90 disabled:opacity-60"
            style={{ background: `linear-gradient(135deg, ${colors.accentFrom}, ${colors.accentTo})` }}
          >
            {guardando ? 'Guardando...' : esEdicion ? 'Guardar cambios' : 'Crear usuario'}
          </button>
          <Link to="/admin/usuarios" className="px-5 py-2.5 text-gray-600 font-medium rounded-lg hover:bg-gray-100">
            Cancelar
          </Link>
        </div>
      </form>

      {esEdicion && !esYo && (
        <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-6">
          <h2 className="text-sm font-semibold text-gray-900 mb-1">Restablecer contraseña</h2>
          <p className="text-xs text-gray-400 mb-3">Define una contraseña nueva para este usuario.</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="password"
              value={passwordNueva}
              onChange={(e) => setPasswordNueva(e.target.value)}
              minLength={8}
              placeholder="Nueva contraseña (mínimo 8 caracteres)"
              className={inputClass}
              style={ring}
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={handleRestablecer}
              className="px-5 py-2.5 font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 whitespace-nowrap"
            >
              Restablecer
            </button>
          </div>
        </section>
      )}
    </div>
  );
};

export default AdminUsuarioForm;
